import React, { useState, useEffect, useMemo, useRef } from "react"
import { Button, Typography, Paper } from "@material-ui/core"
import { RailsForm } from '@support/RailsForm'
import { Form as FinalForm, FormSpy } from "react-final-form"
import Field from '@ui/Fields/Field'
import { format } from "date-fns"
import { fetchIrrigations, buildQueryWithBrackets } from "@models/admin/reports"
import styles from '@theme/styles/forms'
import withStyles from '@material-ui/core/styles/withStyles'
import LoadingComponent from '@ui/LoadingComponent'
import { getToken } from '@services/authStore'
import { useErrorHandler } from '@contexts/ErrorHandlerProvider'


const IrrigationReport = ({ classes }) => {
  const [irrigations, setIrrigations] = useState([])
  const [organizationId, setOrganizationId] = useState('')
  const [dataLoaded, setDataLoaded] = useState(false)
  const [loading, setLoading] = useState(false)
  const handleError = useErrorHandler()
  const prevStatusRef = useRef('active')

  useEffect(() => {
    const fetchData = async () => {
      if (!organizationId) {
        setIrrigations([])
        setDataLoaded(false)
        return
      }

      try {
        const listIrrigations = await fetchIrrigations(organizationId)
        setIrrigations(listIrrigations.map(({ id, name, status }) => ({ id, name, status })))
        setDataLoaded(true)
      } catch (error) {
        handleError(error)
        setDataLoaded(false)
      }
    }

    const timeoutId = setTimeout(() => {
      fetchData()
    }, 1000);

    return () => clearTimeout(timeoutId)
  }, [organizationId])

  const handleSubmit = async (data) => {
    const { start, finish, management_status, ...rest } = data;
    const formattedStart = format(new Date(start), "yyyy-MM-dd");
    const formattedFinish = format(new Date(finish), "yyyy-MM-dd");
    const params = {
      ...rest,
      start: formattedStart,
      finish: formattedFinish,
      token: getToken(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    };

    const query = buildQueryWithBrackets(params)
    const url = `https://reports.cultivointeligente.com.br/run_report/irrigation_report?${query}`

    window.open(url, '_blank')
  }

  const today = useMemo(() => new Date(), [])
  const initialValues = useMemo(() => ({
    start: today,
    finish: today,
    organization_id: '',
    management_status: 'active',
    irrigation_management_ids: []
  }), [today])

  return (
    <>
      {loading ? <LoadingComponent /> : (
        <RailsForm
          component={FinalForm}
          action="create"
          url={`/admin/reports/irrigation_report`}
          handleSubmit={handleSubmit}
          initialValues={initialValues}
          render={({ handleSubmit, values, form }) => {
            if (values.organization_id !== organizationId) {
              setOrganizationId(values.organization_id || '')
            }

            const displayedManagements = irrigations
              .filter(m =>
                values.management_status === 'all'
                  ? true
                  : values.management_status === 'finished'
                    ? m.status === 'finished'
                    : m.status !== 'finished'
              )
              .map(m => ({
                id: m.id,
                name: m.status === 'finished' ? `${m.name} (Finalizado)` : m.name
              }))

            return (
              <Paper className={classes.form__wrapper} elevation={0}>
                <FormSpy
                  subscription={{ values: true }}
                  onChange={({ values }) => {
                    if (values.management_status !== prevStatusRef.current) {
                      prevStatusRef.current = values.management_status
                      form.change('irrigation_management_ids', [])
                    }
                  }}
                />
                <form className={classes.form} onSubmit={handleSubmit}>
                  <header className={classes.form__header}>
                    <Typography component="h2" variant="h5">
                      {I18n.t("activerecord.models.v4/reports.irrigation")}
                    </Typography>
                  </header>
                  <main className={classes.form__fields}>
                    <Field type="date" label="Data Inicial" name="start" required />
                    <Field type="date" label="Data Final" name="finish" required />
                    <Field type="text" label="Organization" name="organization_id" required />
                    {dataLoaded && (
                      <>
                        <Field type="select"
                          data={[
                            { id: 'active', name: 'Ativos' },
                            { id: 'finished', name: 'Finalizados' },
                            { id: 'all', name: 'Todos' }
                          ]}
                          label="Status do Manejo"
                          name="management_status"
                          required
                        />
                        <Field type="multiSelect"
                          data={displayedManagements}
                          label="Manejos de Irrigação"
                          name="irrigation_management_ids"
                          required
                        />
                      </>
                    )}
                  </main>
                  <footer className={classes.form__actions}>
                    <Button
                      className={classes.form__action}
                      color="primary"
                      type="submit"
                      variant="contained"
                    >
                      Gerar Relatório
                    </Button>
                  </footer>
                </form>
              </Paper>
            )
          }}
        />
      )}
    </>
  )
}

export default withStyles(styles)(IrrigationReport)
